import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import Layout from './components/layout/Layout';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PlayersPage from './pages/players/PlayersPage';
import PlayerDetailPage from './pages/players/PlayerDetailPage';
import PlayerFormPage from './pages/players/PlayerFormPage';
import WyscoutPlayerImportPage from './pages/players/WyscoutPlayerImportPage';
import WyscoutPlayerMatchPage from './pages/players/WyscoutPlayerMatchPage';
import ReportsPage from './pages/reports/ReportsPage';
import ReportDetailPage from './pages/reports/ReportDetailPage';
import ReportFormPage from './pages/reports/ReportFormPage';
import PlayerComparisonPage from './pages/players/PlayerComparisonPage';
import TeamSelectionPage from './pages/team/TeamSelectionPage';
import MatchesPage from './pages/matches/MatchesPage';
import MatchDetailPage from './pages/matches/MatchDetailPage';
import CreateMatchPage from './pages/matches/CreateMatchPage';
import EditMatchPage from './pages/matches/EditMatchPage';
import PostMatchPage from './pages/matches/PostMatchPage';
import AIHubPage from './pages/ai/AIHubPage';
import SeasonDashboardPage from './pages/season/SeasonDashboardPage';
import WyscoutMatchPage from './pages/matches/WyscoutMatchPage';
import UsersPage from './pages/admin/UsersPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import SchedulingPage from './pages/admin/SchedulingPage';
import QueuePage from './pages/admin/QueuePage';
import ProfilePage from './pages/profile/ProfilePage';
import PlayerDashboardPage from './pages/player/PlayerDashboardPage';
import PlayerMedicalPage from './pages/player/PlayerMedicalPage';
import PlayerCalendarPage from './pages/player/PlayerCalendarPage';
import CalendarPage from './pages/calendar/CalendarPage';
import LeagueBrowsePage from './domains/league/LeagueBrowsePage';
import LeagueMatchDetailPage from './domains/league/LeagueMatchDetailPage';
import LeaguePlayerDetailPage from './domains/league/LeaguePlayerDetailPage';
import LeagueComparisonPage from './domains/league/LeagueComparisonPage';
import { RoleBasedRedirect, WyscoutRoute } from './routes/guards';
import { FOOTBALL_ROUTE_POLICY } from './routes/footballRoutePolicy';

const LegacyComparisonRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={{pathname: '/comparison', search: location.search}} replace />;
};

const AIHubRedirect: React.FC<{ tab: 'analysis' | 'history' }> = ({ tab }) => (
  <Navigate to={`/ai-hub?tab=${tab}`} replace />
);

const ReportActionRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { type } = useParams();
  const rolesByType: Record<string, readonly string[]> = {
    scouting: ['admin', 'scout'],
    performance: ['admin', 'coach'],
    medical: ['admin', 'medical'],
    training: ['admin', 'coach'],
  };
  return user && rolesByType[type || '']?.includes(user.role) ? <>{children}</> : <Navigate to="/reports" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRoles={['admin', 'manager', 'coach', 'medical']}>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/players"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.players}>
                  <Layout>
                    <PlayersPage mode="squad" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/players/new"
              element={<ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.players.filter(role => role !== 'medical')}><Layout><PlayerFormPage mode="squad" /></Layout></ProtectedRoute>}
            />
            <Route
              path="/players/:id/edit"
              element={<ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.players.filter(role => role === 'admin')}><Layout><PlayerFormPage mode="squad" /></Layout></ProtectedRoute>}
            />
            <Route
              path="/players/:id/wyscout-matches/:matchId"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.players}>
                  <Layout><WyscoutRoute><WyscoutPlayerMatchPage mode="squad" /></WyscoutRoute></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/players/:id"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.players}>
                  <Layout>
                    <PlayerDetailPage mode="squad" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/scouting"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting}>
                  <Layout>
                    <PlayersPage mode="scouting" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/scouting/import"
              element={<ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting.filter(role => role !== 'medical')}><Layout><WyscoutPlayerImportPage /></Layout></ProtectedRoute>}
            />
            <Route
              path="/scouting/new"
              element={<ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting.filter(role => role !== 'medical')}><Layout><PlayerFormPage mode="scouting" /></Layout></ProtectedRoute>}
            />
            <Route
              path="/scouting/:id/edit"
              element={<ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting.filter(role => role === 'admin')}><Layout><PlayerFormPage mode="scouting" /></Layout></ProtectedRoute>}
            />
            <Route
              path="/scouting/:id/wyscout-matches/:matchId"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting}>
                  <Layout><WyscoutRoute><WyscoutPlayerMatchPage mode="scouting" /></WyscoutRoute></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/scouting/:id"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.scouting}>
                  <Layout>
                    <PlayerDetailPage mode="scouting" />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.reports}>
                  <Layout>
                    <ReportsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/:type/:id"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.reports}>
                  <Layout>
                    <ReportDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/:type/new"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.reports}>
                  <Layout>
                    <ReportActionRoute><ReportFormPage /></ReportActionRoute>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:type/:id/edit"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.reports}>
                  <Layout>
                    <ReportActionRoute><ReportFormPage /></ReportActionRoute>
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/players/compare"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.comparison}>
                  <LegacyComparisonRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/comparison"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.comparison}>
                  <Layout>
                    <PlayerComparisonPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.team}>
                  <Layout>
                    <TeamSelectionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/matches"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.matches}>
                  <Layout>
                    <MatchesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/new"
              element={
                <ProtectedRoute requiredRoles={["admin","manager","coach"]}>
                  <Layout>
                    <CreateMatchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/wyscout/:matchId"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.matches}>
                  <Layout>
                    <WyscoutRoute>
                      <WyscoutMatchPage />
                    </WyscoutRoute>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/:id"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.matches}>
                  <Layout>
                    <MatchDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/:id/post-match"
              element={
                <ProtectedRoute requiredRoles={["admin","manager","coach"]}>
                  <Layout>
                    <PostMatchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches/:id/edit"
              element={
                <ProtectedRoute requiredRoles={["admin","manager","coach"]}>
                  <Layout>
                    <EditMatchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/ai-hub" element={
              <ProtectedRoute requiredRoles={['admin', 'manager', 'coach', 'medical']}>
                <Layout><AIHubPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/analysis" element={
              <ProtectedRoute requiredRoles={['admin', 'manager']}>
                <WyscoutRoute><AIHubRedirect tab="analysis" /></WyscoutRoute>
              </ProtectedRoute>
            } />

            <Route
              path="/season"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.season}>
                  <Layout>
                    <WyscoutRoute><SeasonDashboardPage /></WyscoutRoute>
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/league"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.league}>
                  <Layout>
                    <WyscoutRoute><LeagueBrowsePage /></WyscoutRoute>
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/league/matches/:matchId"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.league}>
                  <Layout><WyscoutRoute><LeagueMatchDetailPage /></WyscoutRoute></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/league/players/:playerId"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.league}>
                  <Layout><WyscoutRoute><LeaguePlayerDetailPage /></WyscoutRoute></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/league/comparison"
              element={
                <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.comparison}>
                  <Layout><WyscoutRoute><LeagueComparisonPage /></WyscoutRoute></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/chat-history" element={
              <ProtectedRoute requiredRoles={['admin', 'manager', 'coach', 'scout', 'medical']}>
                <AIHubRedirect tab="history" />
              </ProtectedRoute>
            } />

            <Route
              path="/admin/scheduling"
              element={
                <ProtectedRoute requiredRoles={["admin"]}>
                  <Layout>
                    <SchedulingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/queue"
              element={
                <ProtectedRoute requiredRoles={["admin"]}>
                  <Layout>
                    <QueuePage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRoles={["admin"]}>
                  <Layout>
                    <UsersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute requiredRoles={["admin"]}>
                  <Layout>
                    <AuditLogPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Player portal routes */}
            <Route path="/player" element={
              <ProtectedRoute requiredRoles={['player']}>
                <Layout><PlayerDashboardPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/player/medical" element={
              <ProtectedRoute requiredRoles={['player']}>
                <Layout><PlayerMedicalPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/player/calendar" element={
              <ProtectedRoute requiredRoles={['player']}>
                <Layout><PlayerCalendarPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Calendar (staff) */}
            <Route path="/calendar" element={
              <ProtectedRoute requiredRoles={FOOTBALL_ROUTE_POLICY.calendar}>
                <Layout><CalendarPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/wyscout-matches" element={<Navigate to="/matches" replace />} />

            {/* Redirect root */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* Catch all */}
            <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
